import { ILabel, ILabelType } from 'annotation-tools/annotation-canvas';
import {
  combineLatestTyped,
  filterValue,
  isNumber,
  promiseState,
  useBooleanState,
  useDataStream,
  useStreamEffect,
  useStreamValue
} from 'annotation-tools/utils';
import React, { FC, memo } from 'react';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { viewTexts } from '../../../constants/view-texts';
import { getListItem } from '../../../global-state/database/common/get-list-item';
import {
  ListItemWithPromiseState,
  listItemWithPromiseStateInit
} from '../../../global-state/database/common/list-item-with-status';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import {
  mapListItemLatest,
  mapListLatest
} from '../../../global-state/database/common/map-latest';
import { labelListOf } from '../../../global-state/database/model-label-list';
import { projectImageAnnotationListOf } from '../../../global-state/database/project-image-annotation-list';
import { projectImageListOf } from '../../../global-state/database/project-image-list';
import { imageId$, projectId$ } from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { IAnnotationType } from '../../../types/record/annotation';
import { IProjectImageInfoType } from '../../../types/record/image-info';
import { ProjectImageSettingsDialog } from '../../dialogs/project-image-settings-dialog/project-image-settings-dialog';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { ProjectImageRootSub } from './project-image-root-sub';

export const ProjectImageRoot: FC<{}> = memo(() => {
  const imageList$ = useDataStream<ListWithPromiseState<IProjectImageInfoType>>(
    listWithPromiseStateInit(),
    projectId$.pipe(switchMap(projectImageListOf))
  );

  const image$ = useDataStream<ListItemWithPromiseState<IProjectImageInfoType>>(
    listItemWithPromiseStateInit(),
    combineLatestTyped(imageList$, imageId$).pipe(
      map(([list, id]) => getListItem(list, id))
    )
  );

  const selectedModelId$ = useDataStream<
    ListItemWithPromiseState<number | 'not-selected'>
  >(
    listItemWithPromiseStateInit(),
    image$.pipe(
      map(image => mapListItemLatest(image, e => e.modelId ?? 'not-selected'))
    )
  );

  const labelList$ = useDataStream<ListWithPromiseState<ILabelType>>(
    listWithPromiseStateInit(),
    selectedModelId$.pipe(
      map(id => id.value.latest),
      filter(isNumber),
      switchMap(labelListOf),
      map(list => mapListLatest(list, l => l.map(ILabel)))
    )
  );

  const annotationList$ = useDataStream<ListWithPromiseState<IAnnotationType>>(
    listWithPromiseStateInit(),
    combineLatestTyped(projectId$, imageId$).pipe(
      switchMap(([projectId, imageId]) =>
        projectImageAnnotationListOf(projectId, imageId)
      )
    )
  );

  const [
    settingsDialogIsOpen,
    openSettingsDialog,
    closeSettingsDialog
  ] = useBooleanState(false);

  useStreamEffect(
    selectedModelId$.pipe(
      map(a => a.value.latest),
      distinctUntilChanged(),
      filterValue('not-selected')
    ),
    openSettingsDialog
  );

  /* extracted values */
  const imageList = useStreamValue(imageList$);
  const labelList = useStreamValue(labelList$);
  const annotationList = useStreamValue(annotationList$);
  const image = useStreamValue(image$);
  const selectedModelId = useStreamValue(selectedModelId$);

  useLoadingPage(
    imageList?.status === 'pending' ||
      labelList?.status === 'pending' ||
      annotationList?.status === 'pending' ||
      image?.status === 'pending' ||
      selectedModelId?.status === 'pending'
  );

  return (
    <>
      {imageList === undefined ||
      labelList === undefined ||
      annotationList === undefined ||
      image?.value.latest === undefined ||
      selectedModelId?.value.latest === undefined ? (
        undefined
      ) : labelList.status === promiseState.error ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.labelList} />
      ) : annotationList.status === promiseState.error ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.annotationList} />
      ) : imageList.status === promiseState.error ? (
        <NotFoundTemplate
          message={viewTexts.notFoundMessages.projectImageList}
        />
      ) : (
        <>
          <ProjectImageSettingsDialog
            mode={'init'} // TODO ?
            open={settingsDialogIsOpen}
            onClose={closeSettingsDialog}
            projectImage={image.value.latest}
          />
          <ProjectImageRootSub
            imageList={imageList.value.latest}
            labelList={labelList.value.latest}
            annotationList={annotationList.value.latest}
            image={image.value.latest}
            openSettingsDialog={openSettingsDialog}
          />
        </>
      )}
    </>
  );
});

ProjectImageRoot.displayName = 'ProjectImageRoot';
